import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import toast from 'react-hot-toast';
import { runAction } from '@/utils/errors';
import {
  getEmployees,
  getSmsAvailableAt,
  isSessionInactiveError,
  sendTsdLoginSms,
} from '@/api/employees';
import type { Employee, EmployeeStore } from '@/types/employee';
import { EmployeeRow } from './EmployeeRow';

const patchEmployee = (rows: Employee[], employeeId: string, changes: Partial<Employee>) =>
  rows.map((row) => (row.id === employeeId ? { ...row, ...changes } : row));

const EmployeesPage = () => {
  const { keycloak } = useKeycloak();
  const [rows, setRows] = useState<Employee[]>([]);
  const [stores, setStores] = useState<EmployeeStore[]>([]);
  const [search, setSearch] = useState('');
  const [storeId, setStoreId] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sessionsVerified, setSessionsVerified] = useState(false);
  const rowsRevision = useRef(0);

  const updateEmployee = useCallback((employeeId: string, changes: Partial<Employee>) => {
    rowsRevision.current += 1;
    setRows((current) => patchEmployee(current, employeeId, changes));
  }, []);

  useEffect(() => {
    setSessionsVerified(false);
    if (!keycloak.token) return;
    const controller = new AbortController();
    let refreshing = false;
    setLoading(true);
    const refresh = async () => {
      if (refreshing || controller.signal.aborted) return;
      refreshing = true;
      const revision = rowsRevision.current;
      await runAction(
        async () => {
          const result = await getEmployees(controller.signal);
          if (controller.signal.aborted || revision !== rowsRevision.current) return;
          setRows(result.rows);
          setStores(result.stores);
          setSessionsVerified(true);
        },
        { signal: controller.signal, onError: () => setSessionsVerified(false) },
      ).finally(() => {
        refreshing = false;
        if (!controller.signal.aborted) setLoading(false);
      });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    refresh();
    const timer = window.setInterval(refreshWhenVisible, 15_000);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [keycloak.token]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru-RU');
    return rows
      .filter(
        (employee) =>
          (!storeId || employee.storeId === storeId) &&
          (!query ||
            employee.fullName.toLocaleLowerCase('ru-RU').includes(query) ||
            String(employee.num ?? '').includes(query)),
      )
      .sort(
        (left, right) =>
          Number(right.hasActiveSession) - Number(left.hasActiveSession) ||
          left.fullName.localeCompare(right.fullName, 'ru-RU'),
      );
  }, [rows, search, storeId]);

  const handleSend = useCallback(
    async (employee: Employee) => {
      if (!sessionsVerified || !employee.hasActiveSession) return;
      setSendingId(employee.id);
      await runAction(
        async () => {
          const result = await sendTsdLoginSms(employee.id);
          updateEmployee(employee.id, { smsAvailableAt: result.availableAt });
          toast.success(`Данные для входа отправлены сотруднику ${employee.fullName}`);
        },
        {
          onError: (error) => {
            const availableAt = getSmsAvailableAt(error);
            if (availableAt) updateEmployee(employee.id, { smsAvailableAt: availableAt });
            if (isSessionInactiveError(error)) {
              updateEmployee(employee.id, { hasActiveSession: false });
            }
          },
        },
      ).finally(() => {
        setSendingId(null);
      });
    },
    [sessionsVerified, updateEmployee],
  );

  return (
    <div className="mx-auto w-full max-w-[1600px] text-gray-800">
      <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <label className="flex-1 text-sm font-medium text-gray-600">
            Поиск сотрудника
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="ФИО или табельный номер"
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-[#631d76]"
            />
          </label>
          {stores.length > 1 && (
            <label className="min-w-[280px] text-sm font-medium text-gray-600">
              Магазин
              <select
                value={storeId}
                onChange={(event) => setStoreId(event.target.value)}
                className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 outline-none focus:border-[#631d76]"
              >
                <option value="">Все доступные магазины</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <p className="mb-0 mt-3 text-xs text-gray-500">
          Найдено сотрудников: {filteredRows.length}. Повторная SMS одному сотруднику доступна через
          5 минут.
        </p>
      </section>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-left text-sm">
            <thead className="bg-gray-100 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-3">ФИО</th>
                <th className="px-4 py-3">Табельный номер</th>
                <th className="px-4 py-3">Должность</th>
                <th className="px-4 py-3">Магазин</th>
                <th className="px-4 py-3">Телефон</th>
                <th className="px-4 py-3 text-right">Действие</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Загрузка сотрудников…
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Сотрудники не найдены
                  </td>
                </tr>
              ) : (
                filteredRows.map((employee) => (
                  <EmployeeRow
                    key={employee.id}
                    employee={employee}
                    sending={sendingId === employee.id}
                    sessionsVerified={sessionsVerified}
                    onSend={handleSend}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default EmployeesPage;
